import { useState } from "react";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { cn } from "@/lib/utils";

// Presentation data based on website content
const presentationData = [
  {
    type: "cover",
    title: "Benchr.co",
    subtitle: "E-commerce Competitive Intelligence & Market Analysis Platform",
    description: "Access comprehensive product data, real-time price monitoring, and competitive market analysis from any global e-commerce website.",
    image: "/lovable-uploads/18e820bb-8d7e-4e8c-ad0f-d384b26af6ad.png"
  },
  {
    type: "intro",
    title: "Global E-commerce Intelligence",
    subtitle: "One platform for comprehensive market data",
    points: [
      "Access data from any e-commerce site worldwide",
      "No scraping infrastructure to build or maintain",
      "Real-time monitoring of market changes",
      "Enterprise-grade reliability and coverage"
    ],
    image: "/lovable-uploads/92e9ae59-4c3c-42e8-8269-f0f0189eb0b7.png"
  },
  {
    type: "feature",
    title: "Product Monitoring",
    description: "Access comprehensive product data from any e-commerce site worldwide. Monitor competitors' catalogs, detect new listings, and track inventory changes in real-time.",
    benefits: [
      "Global product data coverage",
      "Catalog comparison tools",
      "New product alerts",
      "Product performance tracking"
    ],
    icon: "BarChart2",
  },
  {
    type: "feature",
    title: "Competitive Pricing Intelligence",
    description: "Access pricing data from any e-commerce site globally. Track pricing strategies across competitors, detect promotional patterns, and optimize your pricing for maximum market impact.",
    benefits: [
      "Global pricing data coverage",
      "Historical pricing data",
      "Discount & promotion detection",
      "Price elasticity analysis"
    ],
    icon: "Activity",
  },
  {
    type: "feature",
    title: "Market Trend Analysis",
    description: "Access traffic data and consumer behavior insights from any e-commerce platform worldwide. Analyze patterns and emerging market trends to stay ahead of the competition.",
    benefits: [
      "Global market data coverage",
      "Consumer behavior insights",
      "Category growth tracking",
      "Seasonal trend prediction"
    ],
    icon: "RefreshCw",
  },
  {
    type: "technology",
    title: "Comprehensive Data Access",
    subtitle: "Global E-commerce Data Catalog",
    description: "Subscribe to our data feeds covering any e-commerce website worldwide. We handle all the data collection, cleaning, and processing, so you can focus on analyzing the insights without worrying about infrastructure.",
    stats: [
      { label: "websites", value: "+5 million" },
      { label: "products", value: "+1 billion" }
    ]
  },
  {
    type: "llm",
    title: "Best E-commerce LLM Technology",
    description: "Our enterprise-grade infrastructure processes product descriptions, reviews, and market data in real-time while routing questions to the optimal LLM for guaranteed accuracy.",
    features: [
      { 
        title: "Real-time Product Processing", 
        subtitle: "Real-time Ingestion",
        tags: ["Product Listings", "Customer Reviews", "Market Reports"],
        stat: "+1.3B products, 3B reviews, 15TB data processed"
      },
      {
        title: "Best LLM Models",
        subtitle: "Multi-Model Routing",
        tags: ["GPT-4 Turbo", "Claude 3 Opus", "Gemini 1.5 Pro", "Llama"],
        stat: "Dynamic optimization for performance and cost"
      },
      {
        title: "0 Hallucination",
        subtitle: "Verification System",
        code: true,
        stat: "100% fact-checked responses"
      }
    ]
  },
  {
    type: "api",
    title: "Global E-commerce API Status",
    subtitle: "API Connection Status",
    sources: [
      { name: "Sephora.com", status: "connected", coverage: "98%" },
      { name: "Amazon.com", status: "connected", coverage: "99%" },
      { name: "Aesop.com", status: "connected", coverage: "95%" },
      { name: "Dyson.com", status: "connected", coverage: "97%" },
      { name: "Uniqlo.com", status: "connected", coverage: "96%" },
      { name: "Target.com", status: "connected", coverage: "98%" },
      { name: "Walmart.com", status: "connected", coverage: "99%" },
      { name: "BestBuy.com", status: "connected", coverage: "97%" }
    ]
  },
  {
    type: "testimonials",
    title: "Client Success Stories",
    subtitle: "Trusted by leading brands worldwide",
    quotes: [
      {
        text: "Benchr helped us optimize our pricing strategy and increased our revenue by 28% in just three months.",
        author: "E-commerce Director",
        company: "Global Beauty Brand"
      },
      {
        text: "The market insights we get from Benchr have transformed our product development cycle and competitive positioning.",
        author: "Head of Product",
        company: "Leading Electronics Manufacturer"
      }
    ]
  },
  {
    type: "cta",
    title: "Ready to get started?",
    subtitle: "Start your free trial today and transform your e-commerce strategy with Benchr's global market intelligence.",
    cta: "Start Free Trial - No Credit Card Required",
    contact: "Contact sales@benchr.co for enterprise inquiries"
  }
];

const CommercialPresentation = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = presentationData.length;

  const nextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (pageIndex: number) => {
    if (pageIndex >= 0 && pageIndex < totalPages) {
      setCurrentPage(pageIndex);
    }
  };

  // Render the current slide based on its type
  const renderSlide = () => {
    const slide = presentationData[currentPage];

    switch (slide.type) {
      case "cover":
        return (
          <div className="flex flex-col justify-center items-center text-center h-full p-8">
            {slide.image && (
              <img 
                src={slide.image} 
                alt="Benchr.co Logo" 
                className="h-20 mb-8"
              />
            )}
            <h1 className="text-5xl font-bold text-benchr-900 dark:text-white mb-4 text-gradient animate-slide-up">
              {slide.title}
            </h1>
            <h2 className="text-2xl font-semibold text-benchr-700 dark:text-benchr-200 mb-8">
              {slide.subtitle}
            </h2>
            <p className="text-xl text-benchr-600 dark:text-benchr-300 max-w-2xl">
              {slide.description}
            </p>
          </div>
        );

      case "intro":
        return (
          <div className="grid md:grid-cols-2 gap-8 h-full p-8">
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl font-bold text-benchr-900 dark:text-white mb-4 text-gradient">
                {slide.title}
              </h2>
              <h3 className="text-xl font-semibold text-benchr-700 dark:text-benchr-200 mb-6">
                {slide.subtitle}
              </h3>
              <ul className="space-y-4">
                {slide.points.map((point, index) => (
                  <li 
                    key={index} 
                    className="flex items-start gap-3"
                  >
                    <div className="h-6 w-6 rounded-full bg-benchr-600 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      {index + 1}
                    </div>
                    <span className="text-benchr-700 dark:text-benchr-300 text-lg">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            {slide.image && (
              <div className="flex items-center justify-center">
                <img 
                  src={slide.image} 
                  alt="E-commerce Intelligence" 
                  className="max-h-[400px] rounded-lg object-contain shadow-lg"
                />
              </div>
            )}
          </div>
        );

      case "feature":
        return (
          <div className="h-full p-8">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-4 mb-6">
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-benchr-600 to-benchr-800 flex items-center justify-center">
                  {slide.icon === "BarChart2" && <BarChart2 className="h-8 w-8 text-white" />}
                  {slide.icon === "Activity" && <Activity className="h-8 w-8 text-white" />}
                  {slide.icon === "RefreshCw" && <RefreshCw className="h-8 w-8 text-white" />}
                </div>
                <h2 className="text-3xl font-bold text-benchr-900 dark:text-white text-gradient">
                  {slide.title}
                </h2>
              </div>
              
              <p className="text-xl text-benchr-600 dark:text-benchr-300 mb-10">
                {slide.description}
              </p>
              
              <div className="grid grid-cols-2 gap-x-10 gap-y-6">
                {slide.benefits.map((benefit, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3"
                  >
                    <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-5 w-5" />
                    </div>
                    <span className="text-benchr-700 dark:text-benchr-300 text-lg font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "technology":
        return (
          <div className="h-full p-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-benchr-900 dark:text-white mb-4 text-gradient text-center">
                {slide.title}
              </h2>
              
              <div className="bg-benchr-900 rounded-xl p-8 shadow-xl mt-6">
                <h3 className="text-2xl font-bold text-white mb-3">
                  {slide.subtitle}
                </h3>
                <p className="text-benchr-200 text-lg mb-6">
                  {slide.description}
                </p>
                
                <div className="flex flex-wrap gap-4 mb-6">
                  {slide.stats.map((stat, index) => (
                    <div key={index} className="bg-benchr-700/50 rounded-full px-5 py-2 flex items-center gap-2">
                      <span className="text-lg font-semibold text-white">{stat.value}</span>
                      <span className="text-sm text-benchr-300">{stat.label}</span>
                    </div>
                  ))}
                </div>
                
                <div className="grid grid-cols-4 gap-3">
                  {["Sephora.com", "Aesop.com", "Dyson.com", "Uniqlo.com"].map((source, index) => (
                    <div key={index} className="bg-benchr-800/50 border border-benchr-700/30 rounded-lg p-3">
                      <h4 className="text-white font-semibold text-sm mb-2">
                        {source}
                      </h4>
                      <p className="text-benchr-300 text-xs">Complete product data updated daily</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case "llm":
        return (
          <div className="h-full p-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-benchr-900 dark:text-white mb-6 text-gradient text-center">
                {slide.title}
              </h2>
              
              <p className="text-lg text-benchr-600 dark:text-benchr-300 mb-8 text-center max-w-3xl mx-auto">
                {slide.description}
              </p>
              
              <div className="grid grid-cols-3 gap-6">
                {slide.features.map((feature, index) => (
                  <div key={index} className="bg-white dark:bg-benchr-800 rounded-xl p-5 shadow-lg border border-benchr-100 dark:border-benchr-700/40">
                    <h3 className="text-xl font-bold text-benchr-600 mb-4">{feature.title}</h3>
                    
                    <div className="flex items-center gap-2 mb-3">
                      {index === 0 && <Database className="w-5 h-5 text-benchr-600" />}
                      {index === 1 && <ShoppingCart className="w-5 h-5 text-benchr-600" />}
                      {index === 2 && <ShieldCheck className="w-5 h-5 text-benchr-600" />}
                      <span className="font-semibold">{feature.subtitle}</span>
                    </div>
                    
                    {feature.tags && (
                      <div className="space-y-2 mt-3 mb-4">
                        {feature.tags.map((tag, idx) => (
                          <div key={idx} className="inline-block bg-benchr-100 dark:bg-benchr-700/40 text-benchr-700 dark:text-benchr-300 rounded-full px-3 py-1 text-sm mr-2 mb-2">
                            {tag}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {feature.code && (
                      <div className="mt-3 mb-4 font-mono text-xs p-2 bg-benchr-50 dark:bg-benchr-900/40 rounded-md overflow-x-auto">
                        <div className="text-blue-600 dark:text-blue-400">agent_1: Located product</div>
                        <div className="text-violet-600 dark:text-violet-400">agent_2: Verified pricing</div>
                        <div className="text-green-600 dark:text-green-400">agent_3: Confirmed</div>
                      </div>
                    )}
                    
                    <p className="mt-3 text-benchr-600 dark:text-benchr-300 text-sm">
                      <span className="font-bold text-green-600">{feature.stat}</span>
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "api":
        return (
          <div className="h-full p-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-benchr-900 dark:text-white mb-4 text-gradient text-center">
                {slide.title}
              </h2>
              <h3 className="text-xl font-semibold text-benchr-700 dark:text-benchr-200 mb-8 text-center">
                {slide.subtitle}
              </h3>
              
              <div className="grid grid-cols-2 gap-4 max-w-3xl mx-auto">
                {slide.sources.map((source, index) => (
                  <div key={index} className="flex items-center justify-between bg-white dark:bg-benchr-800/50 rounded-lg p-4 border border-benchr-100 dark:border-benchr-700/30">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-3 h-3 rounded-full",
                        source.status === "connected" ? "bg-green-500" : "bg-amber-500"
                      )} />
                      <span className="font-medium text-benchr-800 dark:text-white">{source.name}</span>
                    </div>
                    <div className="text-benchr-600 dark:text-benchr-300 text-sm font-medium">
                      {source.coverage} coverage
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "testimonials":
        return (
          <div className="h-full p-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-benchr-900 dark:text-white mb-4 text-gradient text-center">
                {slide.title}
              </h2>
              <h3 className="text-xl font-semibold text-benchr-700 dark:text-benchr-200 mb-10 text-center">
                {slide.subtitle}
              </h3>
              
              <div className="grid grid-cols-2 gap-8">
                {slide.quotes.map((quote, index) => (
                  <div key={index} className="bg-white dark:bg-benchr-800 rounded-xl p-6 shadow-lg border border-benchr-100 dark:border-benchr-700/40 relative">
                    <div className="absolute -top-4 left-6 text-4xl text-benchr-500">"</div>
                    <p className="text-lg text-benchr-700 dark:text-benchr-300 mb-6 pt-3">
                      {quote.text}
                    </p>
                    <div>
                      <div className="font-bold text-benchr-900 dark:text-white">
                        {quote.author}
                      </div>
                      <div className="text-benchr-600 dark:text-benchr-400 text-sm">
                        {quote.company}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case "cta":
        return (
          <div className="flex flex-col justify-center items-center text-center h-full p-8">
            <h2 className="text-4xl font-bold text-benchr-900 dark:text-white mb-4 text-gradient">
              {slide.title}
            </h2>
            <p className="text-xl text-benchr-600 dark:text-benchr-300 max-w-2xl mb-10">
              {slide.subtitle}
            </p>
            
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.open(
                  'https://calendar.google.com/calendar/appointments/schedules/AcZssZ0ixREziCGTZSnjXhuPB0TBIti2X5Ks-ATre2wgAkuMuHqJ4XWW7hldUQxV7WW_GMwHwd9IwfBm?gv=true',
                  'BookADemo',
                  'width=1400,height=900'
                );
              }}
              className="group inline-flex items-center justify-center px-6 py-3.5 bg-[#1e70d1] hover:bg-[#1e70d1]/90 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-300 ease-in-out w-full sm:w-auto pulse-soft"
            >
              Book a Demo
              <ArrowRight size={18} className="ml-2 transition-transform group-hover:translate-x-1" />
            </a>
            
            <p className="text-benchr-500 dark:text-benchr-400">
              {slide.contact}
            </p>
          </div>
        );

      default:
        return (
          <div className="flex justify-center items-center h-full">
            <p>Slide content not available</p>
          </div>
        );
    }
  };

  return (
    <div className="w-full h-[calc(100vh-8rem)] max-w-6xl mx-auto bg-benchr-50 dark:bg-benchr-900 rounded-xl shadow-2xl overflow-hidden border border-benchr-200 dark:border-benchr-700">
      {/* Slide content */}
      <div className="relative h-full">
        {/* Main slide content */}
        <div className="h-[calc(100%-4rem)] overflow-auto">
          {renderSlide()}
        </div>
        
        {/* Navigation controls */}
        <div className="h-16 bg-white dark:bg-benchr-800 border-t border-benchr-200 dark:border-benchr-700 flex items-center justify-between px-6">
          {/* Page indicators */}
          <div className="flex items-center gap-2">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToPage(index)}
                className={cn(
                  "w-2.5 h-2.5 rounded-full transition-all",
                  currentPage === index 
                    ? "bg-benchr-600 scale-125" 
                    : "bg-benchr-300 dark:bg-benchr-600/40 hover:bg-benchr-400"
                )}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          
          {/* Page counter */}
          <div className="text-benchr-600 dark:text-benchr-300 font-medium">
            {currentPage + 1} / {totalPages}
          </div>
          
          {/* Navigation buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={prevPage}
              disabled={currentPage === 0}
              className={cn(
                "p-2 rounded-full transition-all",
                currentPage === 0
                  ? "text-benchr-400 dark:text-benchr-600 cursor-not-allowed"
                  : "text-benchr-700 dark:text-benchr-300 hover:bg-benchr-100 dark:hover:bg-benchr-700"
              )}
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages - 1}
              className={cn(
                "p-2 rounded-full transition-all",
                currentPage === totalPages - 1
                  ? "text-benchr-400 dark:text-benchr-600 cursor-not-allowed"
                  : "text-benchr-700 dark:text-benchr-300 hover:bg-benchr-100 dark:hover:bg-benchr-700"
              )}
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Import required icons
import { CheckCircle, BarChart2, Activity, RefreshCw, Database, ShoppingCart, ShieldCheck, ArrowRight } from "lucide-react";

export default CommercialPresentation;
